import { Grid, withStyles } from '@material-ui/core'
import { getStyles } from 'isotope-client'
import PropTypes from 'prop-types'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { compose } from 'redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import Detail from '../../planning/component/detail/Detail'
import { ACTION_AFFECTATION, TYPE_ENTITE_TACHE } from '../../../../utils/constants'
import AutocompleteMulti from '../../../../components/form/AutocompleteMulti'
import { ReactComponent as Jardinier } from '../../../../icons/jardinier.svg'
import { injectTypeTacheValueList } from '../../../common/valueLists/valueListInjectors'
import { LocalDate } from '../../../../utils/date/local-date'
import { DateFormat } from '../../../../utils/date/model/date'
import Radio from '../../../../components/form/Radio'
import { connect } from 'react-redux'
import FormattedMessageWithBold from '../../../../components/FormattedMessageWithBold'
import Typography from '@material-ui/core/Typography/index'
import PickingIcon from '../../../../components/icon/PickingIcon'

export const AFFECTATION_FORM = 'AFFECTATION_FORM'

const styles = () => getStyles({
	container: {
		margin: '1.5em'
	},
	form: {
		width: '100%',
		margin: 15
	},
	details: {
		width: 'initial !important'
	},
	formInput: {
		'& > div': {
			padding: 0
		},
		'& label': {
			fontSize: '0.75em'
		},
		'&:last-child': {
			marginBottom: 0
		},
		'& > p': {
			marginBottom: 5
		}
	},
	icon: {
		height: 24,
		width: 24
	},
	radio: {
		width: '100%',
		'& > div': {
			'& > label': {
				width: 180,
				'& span': {
					fontSize: '12px !important'
				}
			}
		}
	}
})

const validate = values => {
	const errors = {}
	if (!values['usersAutocomplete'] || (values['usersAutocomplete'] && values['usersAutocomplete'].length === 0)) {
		errors['usersAutocomplete'] = { id: 'gestionTaches.ecranPlanification.panels.validate.champVide' }
	}
	return errors
}

/**
 * Méthode permettrant d'obtenir les utilisateurs communs entre plusieurs tâches
 * @param users les utilisateurs possibles
 * @param taches les tâches pour lesquelles on veut les utilisateurs en commun
 * @returns {*[]|*[]}
 */
const getRessourcesCommunes = (users, taches) => {
	if (users.length > 0) {
		const ressourcesSet = taches
			.map(tache => new Set(tache.affectations))
			.reduce((acc, curr) => new Set([...acc].filter(x => curr.has(x))))

		return [...ressourcesSet]
			.map(r => users.find(u => u.alias === r))
			.filter(r => !!r)
	}
	return []
}

const AffectationForm = ({ handleSubmit, classes, taches, typeTacheList, users, affectOrDesaffect, autocompleteOptions }) => {
	const getUsers = () => Promise.resolve(autocompleteOptions)

	const sections = [
		{
			icon: <PickingIcon className={classes.icon} />,
			title: <FormattedMessage id="gestionTaches.ecranPlanification.panels.sections.taches" />,
			content:
				<ul>
					{
						taches.map(item => {
							const task = typeTacheList.find(typeTache => typeTache.code === item.type)

							return (
								<li style={{ paddingBottom: '5px' }} key={item.id}>
									{item.typeLieu === TYPE_ENTITE_TACHE.FERME
										? <FormattedMessage
											id="tourPlaine.form.moveTask.sectionOne.body.taskPlannedFarm"
											values={{
												task: task.label,
												date: LocalDate.fromAPI(item.date).toDisplayDate(DateFormat.SHORT_DATE)
											}}
										/>
										: <FormattedMessage
											id="tourPlaine.form.moveTask.sectionOne.body.taskPlanned"
											values={{
												task: task.label,
												date: LocalDate.fromAPI(item.date).toDisplayDate(DateFormat.SHORT_DATE),
												crop: item.culture,
												board: item.lieu,
												size: item.surface
											}}
										/>
									}

								</li>
							)
						})
					}
				</ul>
		},
		{
			icon: <Jardinier className={classes.icon} />,
			title: <FormattedMessage id="gestionTaches.ecranPlanification.panels.sections.ressources" />,
			content:
				<Grid className={classes.formInput}>
					<Grid>
						<Field name="affectOrDesaffect" component={Radio}
							   choices={[{
								   value: ACTION_AFFECTATION.AFFECTER,
								   label: <FormattedMessage id="gestionTaches.ecranPlanification.panels.content.affectation" />
							   }, {
								   value: ACTION_AFFECTATION.DESAFFECTER,
								   label: <FormattedMessage id="gestionTaches.ecranPlanification.panels.content.desaffectation" />
							   }]}
							   row
							   style={{ justifyContent: 'space-between' }}
							   containerClass={classes.radio}
						/>
					</Grid>

					<Grid>
						{affectOrDesaffect === ACTION_AFFECTATION.AFFECTER && getRessourcesCommunes(users, taches).length > 0 &&
							<Typography style={{ paddingLeft: 5, paddingBottom: 10, fontSize: 12 }}>
								<FormattedMessageWithBold id="gestionTaches.ecranPlanification.panels.content.ressources" values={{ ressources: getRessourcesCommunes(users, taches).map(r => r.label).join(', ') }} />
							</Typography>}
						<Field
							label={affectOrDesaffect === ACTION_AFFECTATION.AFFECTER
								? <FormattedMessage id="gestionTaches.ecranPlanification.panels.multiSelectLabel.userAffectation" />
								: <FormattedMessage id="gestionTaches.ecranPlanification.panels.multiSelectLabel.userDesaffectation" />}
							name="usersAutocomplete"
							component={AutocompleteMulti}
							getOptions={getUsers}
							forceGetOptions
							formName={AFFECTATION_FORM}
						/>
					</Grid>
				</Grid>
		}
	]

	return (
		<form onSubmit={handleSubmit} className={classes.form}>
			<Detail sections={sections} containerClassName={classes.details} />
		</form>
	)

}

AffectationForm.propType = {
	classes: PropTypes.object,
	handleSubmit: PropTypes.func
}

const mapStateToProps = (state, { taches, users }) => {
	const formSelector = formValueSelector(AFFECTATION_FORM)
	const affectOrDesaffect = formSelector(state, 'affectOrDesaffect')
	const oldRessources = getRessourcesCommunes(users, taches)
	const oldRessourcesAlias = oldRessources.map(o => o.alias)

	if (affectOrDesaffect === ACTION_AFFECTATION.DESAFFECTER) {
		return {
			affectOrDesaffect,
			initialValues: {
				affectOrDesaffect: ACTION_AFFECTATION.DESAFFECTER,
				usersAutocomplete: oldRessources
			},
			autocompleteOptions: users
		}
	} else {
		return {
			affectOrDesaffect,
			initialValues: {
				affectOrDesaffect: ACTION_AFFECTATION.AFFECTER
			},
			autocompleteOptions: users.filter(u => !oldRessourcesAlias.includes(u.alias))
		}
	}
}

export default compose(
	connect(mapStateToProps),
	withStyles(styles),
	injectTypeTacheValueList,
	reduxForm({
		form: AFFECTATION_FORM,
		validate,
		enableReinitialize: true
	})
)(AffectationForm)
